import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { Announcement } from '../../models/announcements';
import { AnnouncementsService } from '../../services/announcements.service';

@Component({
  selector: 'app-announcement-details',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './announcement-details.component.html',
  styleUrl: './announcement-details.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AnnouncementDetailComponent implements OnChanges {
  private announcementService = inject(AnnouncementsService);
  private sanitizer = inject(DomSanitizer);
  private destroy$ = new Subject<void>();

  @Input() announcementId!: number | undefined;
  @Input() announcement!: Announcement | undefined;
  @Output() popupClosed = new EventEmitter<void>();

  announcementDetail: Announcement | null = null;
  announcementMessageSafeHtml: SafeHtml = '';
  loading = false;
  hasRecipients = false;
  isRecipientsExpanded = false;

  ngOnChanges(changes: SimpleChanges): void {
    // If full announcement object is provided, use it directly
    if (changes['announcement'] && this.announcement) {
      this.announcementDetail = this.announcement;
      this.announcementMessageSafeHtml = this.sanitizer.bypassSecurityTrustHtml(
        this.announcement.message || '',
      );
      this.hasRecipients =
        this.announcement.recipients?.original_recipients?.connections?.length >
          0 ||
        this.announcement.recipients?.original_recipients?.users?.length > 0;
      this.loading = false;
    }
    // Otherwise, fetch by ID
    else if (
      changes['announcementId'] &&
      this.announcementId &&
      !this.announcement
    ) {
      this.fetchAnnouncement();
    }
  }

  fetchAnnouncement() {
    if (!this.announcementId) return;

    this.loading = true;

    this.announcementService
      .getAnnouncementDetailById(this.announcementId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.announcementDetail = res;
          this.announcementMessageSafeHtml =
            this.sanitizer.bypassSecurityTrustHtml(res.message || '');
          this.hasRecipients = false; // API response doesn't include recipients
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  getRecipients(): string[] {
    if (!this.hasRecipients || !this.announcementDetail) return [];
    const announcement = this.announcementDetail as Announcement;
    const recipients: string[] = [];
    announcement.recipients?.original_recipients?.connections?.forEach(
      (conn) => {
        recipients.push(conn);
      },
    );
    announcement.recipients?.original_recipients?.users?.forEach((user) => {
      recipients.push(user);
    });
    return recipients;
  }

  toggleRecipients(): void {
    this.isRecipientsExpanded = !this.isRecipientsExpanded;
  }
}
